// TODO in SL-5404: Rename this and make it the step 5 component

import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import _isEmpty from "lodash/isEmpty";
import _omit from "lodash/omit";
import cx from "classnames";
import { useTranslation } from "i18n";
import { useHistory } from "react-router-dom";
import { useRecurly } from "@recurly/react-recurly";
import { RecurlyError } from "@recurly/recurly-js/lib/error";
import { TokenPayload } from "@recurly/recurly-js/lib/token";
import {
  useDynamicWindowWidthFor,
  useMobileKeyboardVisibility,
  useScrollToElement,
} from "shared-hooks";
import {
  captureExceptionEvent,
  FEATURE_FLAGS,
  useLaunchDarklyFlags,
} from "shared-services";
import SummaryBlock from "../SummaryBlock";
import checkPaymentMethod from "../../utils/checkPaymentMethod";
import DirectDebitConfirmPage from "../DirectDebitCofirmPage";
import {
  directDebitInitialValues,
  FieldNames,
  PlanType,
} from "../../constants";
import { Spinner } from "../../../../Atoms/Spinner/Spinner";
import { BillingUser, billingUserMap } from "translations/src/models/billing";
import StickyCTABar from "../../../StickyCTABar";
import {
  CheckoutFlow,
  CommonErrorPopup,
  SCREEN_WIDTH_ENUM,
} from "../../../../../index";
import { billingService } from "../../services";
import usePreventLeaveBillingPage from "../../hooks/usePreventLeaveBillingPage";
import { useBillingTranslations } from "../../hooks/useBillingTranslations";
import { BillingContext } from "../../BillingContext";
import { useGetSubscriptions } from "../../hooks/useGetSubscriptions";
import { useGetSummary } from "../../hooks/useGetSummary";
import { useCpaOptIn } from "../../hooks/useCpaOptIn";
import {
  CurrencyType,
  ISubscription,
  ISubscriptionState,
  ISummary,
  ITokenPayload,
  PlanCodeMethod,
  PlanCodePeriod,
} from "../../types";
import useBillingFormValues from "../../hooks/useBillingFormValues";
import useBillingFormValidation from "../../hooks/useBillingFormValidation";
import getInvalidCardFields from "../../utils/getInvalidCardFields";
import ThreeDSecurePage from "../ThreeDSecurePage";
import useCheckoutPreview from "../../hooks/useCheckoutPreview";
import useReinitializeCardForm from "../../hooks/useReinitializeCardForm";
import RecurlyWrapper from "../RecurlyWrapper";
import { getPurchaseType } from "../../utils/getPurchaseType";
import generateBillingInfo from "../../utils/generateBillinginfo";
import BillingAndPaymentForm from "../BillingAndPaymentForm";
import { PaymentErrorModal } from "../PaymentErrorModal";
import PageTitle from "../../../Page/PageTitle/PageTitle";
import PriceInfo from "./PriceInfo";
import { useExistingMethodCountryCode } from "../../hooks/useExistingMethodCountryCode";
import { useGetExistingPaymentMethod } from "../../hooks/useGetExistingPaymentMethod";
import { useGetRecurlyDetails } from "../../hooks/useGetRecurlyDetails";
import { removeDDiscountCode } from "../../utils/removeDDiscountCode";

interface Props {
  subscriptions: ISubscription[];
  placeholderSubscription?: ISubscription;
  summary: ISummary;
  isSubscriptionExpired: boolean;
}

export const getEnableExistingPaymentMethod = (checkoutFlow: CheckoutFlow) => {
  return (
    checkoutFlow === CheckoutFlow.AccountSettings ||
    checkoutFlow === CheckoutFlow.Default ||
    checkoutFlow === CheckoutFlow.GraduateTransfer ||
    checkoutFlow === CheckoutFlow.ChangePlan
  );
};

const AddOnCheckout: FC<React.PropsWithChildren<Props>> = ({
  subscriptions,
  placeholderSubscription,
  summary,
  isSubscriptionExpired,
}) => {
  const { t } = useTranslation();
  const {
    postCheckoutCallback,
    successCallback,
    goBack,
    goBackText,
    checkoutText,
    navigateToCompletePage,
    planTypeFilter,
    useExistingPaymentMethod,
    checkoutFlow,
    billingUserType,
    planPeriodData,
    setPlanPeriodData,
    customTexts,
  } = useContext(BillingContext);
  const isMobileView = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.SM);
  const mobileKeyboardIsOpen = useMobileKeyboardVisibility(isMobileView);
  const [plan, setPlan] = useState<ISubscription | undefined>(
    placeholderSubscription
  );
  const [isFailed, setIsFailed] = useState<boolean>();
  const [recurlyErrorMsg, setRecurlyErrorMsg] = useState("");
  const [isDirectDebitPreview, setIsDirectDebitPreview] = useState<boolean>();
  const [formSubmissionState, setFormSubmissionState] = useState<{
    isSubmitted: boolean;
    isSubmitting: boolean;
  }>({
    isSubmitted: false,
    isSubmitting: false,
  });
  const [recurlyToken, setRecurlyToken] = useState<string>("");
  const [threeDSToken, setThreeDSToken] = useState<string>("");
  const [cardDetailError, setCardDetailError] = useState<
    Record<string, string>
  >({});
  const { mutate: optInCpa } = useCpaOptIn();
  const isAnnual = planPeriodData.selectedPeriod === PlanCodePeriod.Annual;
  const [paymentMethod, setPaymentMethod] = useState<PlanCodeMethod>();
  const [isFormOpen, setIsFormOpen] = useState(
    !!placeholderSubscription && !!paymentMethod
  );
  const currencySymbol = placeholderSubscription?.currencySymbol;
  const htmlFormRef = useRef<HTMLFormElement>(null);
  const recurly = useRecurly();
  const setPreventLeave = usePreventLeaveBillingPage();
  const {
    texts: {
      common,
      billingForm,
      paymentDetailForm,
      directDebitForm,
      validation,
    },
  } = useBillingTranslations(BillingUser.Common);
  const isECPModalVariantFlagActive = useLaunchDarklyFlags(
    FEATURE_FLAGS.ECPModalVariant
  );

  const { formValues, setFieldValue, setMultipleFields } =
    useBillingFormValues();
  const isDirectDebit = checkPaymentMethod(
    PlanCodeMethod.DirectDebit,
    plan?.code
  );
  const isSepa = plan?.planType === PlanType.Sepa;
  const validationMessages = useBillingFormValidation(
    formValues,
    { ...billingForm, ...directDebitForm },
    validation,
    plan?.planType
  );

  const existingPayMethodCountryCode = useExistingMethodCountryCode();
  const selectedCountryCode = useExistingPaymentMethod
    ? existingPayMethodCountryCode
    : formValues[FieldNames.country];

  const {
    price,
    userCouponCodesObject,
    userCouponCodesStrings,
    hasAccountDiscount,
    couponErrors,
    setCoupon,
    removeCoupon,
    isCheckoutPreviewRequestLoading,
    refetchPrices,
  } = useCheckoutPreview(
    placeholderSubscription?.id,
    summary.discounts,
    planPeriodData.selectedAddOns,
    useExistingPaymentMethod
      ? existingPayMethodCountryCode
      : formValues[FieldNames.country]
  );
  const { cardFormIsVisible, reinitializeForm } = useReinitializeCardForm();
  const { ref: sectionRef, scrollToElement: scrollToFormSection } =
    useScrollToElement({ block: "start", inline: "start" });
  const history = useHistory();
  // implemented renewal call logic directly because useMutation was messing with billingService's context.
  const [isRenewalLoading, setIsRenewalLoading] = useState(false);
  const [renewalError, setRenewalError] = useState("");

  const addOnsCodes = planPeriodData.selectedAddOns.map(({ code }) => code);

  const trackingData = useMemo(
    () => ({
      memberType: billingUserType,
      purchaseType: getPurchaseType(checkoutFlow),
      paymentPeriod: plan?.code,
      paymentValue: plan?.amount,
      discountValue: price?.discount,
      promoCode: userCouponCodesStrings,
      addOns: addOnsCodes,
      addOnData: planPeriodData.selectedAddOns,
    }),
    [
      billingUserType,
      checkoutFlow,
      plan?.code,
      plan?.amount,
      price?.discount,
      userCouponCodesStrings,
      addOnsCodes,
      planPeriodData.selectedAddOns,
    ]
  );

  useEffect(() => {
    if (planTypeFilter) {
      setPlan(undefined);
      setIsFormOpen(false);
    }
  }, [planTypeFilter]);

  const couponBag = {
    hasAccountDiscount,
    userCouponCodesObject,
    setCoupon,
    removeCoupon,
    couponErrors,
    isCheckoutPreviewRequestLoading,
  };

  const formErrors = {
    ...(formSubmissionState.isSubmitted ? validationMessages : {}),
    ...cardDetailError,
  };

  const formWrapperClassName = cx("flex justify-between mb-10", {
    "flex-row-reverse flex-1": !isMobileView,
    "flex-col": isMobileView,
  });

  const genericErrorMsg = t("common:billing.genericErrorMessage");

  const failurePurchaseAction = useCallback(
    (err?: string) => {
      setThreeDSToken("");
      setIsFailed(true);
      setRecurlyErrorMsg(err || genericErrorMsg);
      setFormSubmissionState({
        isSubmitted: true,
        isSubmitting: false,
      });
      reinitializeForm();
    },
    [setThreeDSToken, setIsFailed, reinitializeForm]
  );

  const purchase = useCallback(
    async (token: string, threeDsToken?: string) => {
      try {
        const basePayload = {
          planId: plan?.id || "",
          couponCodes: userCouponCodesStrings,
          addOns: addOnsCodes,
        };
        const result = await billingService.purchase({
          token,
          ...(threeDsToken && { threeDsToken }),
          ...basePayload,

          planCurrency: plan?.currency as CurrencyType,
        });

        if (result?.data?.RecurlyError || result?.RecurlyError) {
          failurePurchaseAction(
            result?.data?.RecurlyError?.message || result?.RecurlyError?.message
          );
        }

        if (result?.threeDSActionRequired && result?.actionTokenId) {
          setRecurlyToken(token);
          setThreeDSToken(result.actionTokenId);

          return;
        }

        if (result?.tokenId) {
          if (postCheckoutCallback) {
            await postCheckoutCallback(
              {
                planId: plan?.id,
                planCurrency: plan?.currency,
                couponCodes: removeDDiscountCode(userCouponCodesStrings),
                addOns: addOnsCodes,
                billingInfoId: result.billingInfoId,
              },
              trackingData
            );
          }

          optInCpa(true);

          setPreventLeave(false);

          if (successCallback) {
            successCallback();
          }
        }

        setFormSubmissionState({
          isSubmitted: true,
          isSubmitting: false,
        });

        if (result?.tokenId) {
          // to render <SuccessPage /> on separate route
          navigateToCompletePage?.(isDirectDebit);
        }
      } catch (e) {
        captureExceptionEvent(e);
        failurePurchaseAction();
      }
    },
    [
      addOnsCodes,
      failurePurchaseAction,
      hasAccountDiscount,
      isDirectDebit,
      navigateToCompletePage,
      optInCpa,
      plan?.currency,
      plan?.id,
      postCheckoutCallback,
      setPreventLeave,
      successCallback,
      trackingData,
      userCouponCodesStrings,
    ]
  );

  const removeInvalidCardError = useCallback(
    (field: string) => {
      setCardDetailError((values) => _omit(values, field));
    },
    [setCardDetailError]
  );

  const tokenHandler =
    (failedCallback?: () => void) =>
    async (err: RecurlyError | null, token: TokenPayload) => {
      if (err) {
        setFormSubmissionState({
          isSubmitted: true,
          isSubmitting: false,
        });

        if (err.code !== "validation") {
          // card/debit form validation errors scroll to form element instead of showing pop up
          setRecurlyErrorMsg(err.message || genericErrorMsg);
        }

        setCardDetailError(
          getInvalidCardFields(
            err.fields || [],
            {
              ...paymentDetailForm,
              ...directDebitForm,
              zipLabel: billingForm.zipLabel,
            },
            validation
          )
        );

        if (failedCallback) {
          failedCallback();
        }
      } else {
        await purchase(token.id);
      }
    };

  const enableExistingPaymentMethod =
    getEnableExistingPaymentMethod(checkoutFlow);

  const {
    data: existingPaymentMethodData,
    isLoading: existingPaymentMethodIsLoading,
    isError: existingPaymentMethodIsError,
  } = useGetExistingPaymentMethod(enableExistingPaymentMethod);

  const submitForm = async (e?: SyntheticEvent<any>) => {
    setIsFailed(false);
    setFormSubmissionState({
      isSubmitted: true,
      isSubmitting: true,
    });

    e?.preventDefault();

    if (useExistingPaymentMethod && enableExistingPaymentMethod) {
      setIsRenewalLoading(true);
      const planId = plan?.id ?? "";

      try {
        if (
          checkoutFlow !== CheckoutFlow.GraduateTransfer &&
          checkoutFlow !== CheckoutFlow.ChangePlan
        ) {
          // Skip "purchase" preset endpoint for grads conversion; they only need the call to `complete` if they have existing payment method
          const renewalData =
            await billingService.renewSubscriptionWithExisting({
              planId,
              couponCodes: userCouponCodesStrings,
              addOns: addOnsCodes,
            });

          if (!renewalData.accountId) {
            setRenewalError(genericErrorMsg);

            return;
          }
        }

        if (postCheckoutCallback) {
          await postCheckoutCallback(
            {
              planId,
              planCurrency: plan?.currency,
              couponCodes: removeDDiscountCode(userCouponCodesStrings),
              planTypeFilter,
              addOns: addOnsCodes,
            },
            trackingData
          );
        }

        setIsRenewalLoading(false);

        if (navigateToCompletePage) {
          navigateToCompletePage(false);

          return;
        } else {
          history.push("/myhome/account-settings");
        }
      } catch {
        setRenewalError(genericErrorMsg);

        return;
      }
    }

    if (!_isEmpty(validationMessages) || !_isEmpty(cardDetailError)) {
      setFormSubmissionState({
        isSubmitted: true,
        isSubmitting: false,
      });

      return;
    }

    if (isDirectDebit && !isDirectDebitPreview) {
      setIsDirectDebitPreview(true);

      return;
    }

    if (isDirectDebit && isDirectDebitPreview) {
      recurly.bankAccount.token(
        generateBillingInfo(formValues, isSepa),
        tokenHandler(() => {
          setIsDirectDebitPreview(false);
        })
      );

      return;
    }

    recurly.token(htmlFormRef.current as HTMLFormElement, tokenHandler());
  };

  const submitThreeDSecure = useCallback(
    async (threeDSToken: ITokenPayload) => {
      await purchase(recurlyToken, threeDSToken.id);
    },
    [recurlyToken, purchase]
  );

  const handlePlanSelect = (
    paymentMethod: PlanCodeMethod,
    planPeriod: PlanCodePeriod
  ) => {
    const selectedPlan = subscriptions.find(
      (sub) =>
        sub.code.includes(planPeriod) &&
        checkPaymentMethod(paymentMethod, sub.code)
    );

    if (selectedPlan) {
      setPlan(selectedPlan);

      setIsFormOpen(true);
    }
  };

  useEffect(() => {
    if (isMobileView && isFormOpen) {
      scrollToFormSection();
    }
  }, [isFormOpen, paymentMethod]);

  const handleChangePlanMethod = (paymentMethodType: PlanCodeMethod) => {
    setPaymentMethod(paymentMethodType);
    setFormSubmissionState({
      isSubmitted: false,
      isSubmitting: false,
    });
    setMultipleFields(directDebitInitialValues);

    handlePlanSelect(paymentMethodType, planPeriodData.selectedPeriod!);
  };

  const handleBackButton = () => {
    if (isECPModalVariantFlagActive)
      setPlanPeriodData({ selectedPeriod: undefined, selectedAddOns: [] });

    return goBack && goBack();
  };

  const handleContinueButton = (e: SyntheticEvent<Element, Event>) => {
    return submitForm(e);
  };

  const directDebitPreviewOnEdit = () => {
    setFormSubmissionState({
      isSubmitted: true,
      isSubmitting: false,
    });
    setIsDirectDebitPreview(false);
  };

  const getBackBtnText = () => {
    if (isMobileView && isFormOpen) {
      return common.backMobile;
    }

    if (goBackText) {
      return goBackText;
    }

    return common.back;
  };

  const getCheckoutBtnText = () => {
    if (useExistingPaymentMethod) {
      return t("common:button.label.continue");
    }

    if (checkoutText) {
      return checkoutText;
    }

    return common.submit;
  };

  if (isRenewalLoading) return <Spinner />;

  if (renewalError) {
    return (
      <CommonErrorPopup
        errorStatusCode={400}
        popupProps={{
          close: () => history.go(0),
        }}
        alertMessageProps={{
          buttons: [
            {
              name: t("common:button.label.close"),
              type: "primary",
              click: () => history.go(0),
            },
          ],
        }}
      />
    );
  }

  if (threeDSToken && !isFailed)
    return (
      <ThreeDSecurePage
        threeDSToken={threeDSToken}
        submitThreeDSecure={submitThreeDSecure}
        onThreeDSecureError={failurePurchaseAction}
        threeDSClassName="h-[800px] m-auto w-full md:w-[400px]"
      />
    );

  if (isDirectDebitPreview && !isFailed)
    return (
      <DirectDebitConfirmPage
        name={`${summary.firstName} ${summary.lastName}`}
        email={summary.email}
        accountName={formValues[FieldNames.accountName]}
        sortCode={formValues[FieldNames.sortCode]}
        accountNumber={
          formValues[FieldNames.accountNumber] || formValues[FieldNames.iban]
        }
        directDebitPreviewOnEdit={directDebitPreviewOnEdit}
        submitForm={submitForm}
        setPreventLeave={setPreventLeave}
        planType={plan?.planType}
      />
    );

  const planName = t("common:billing.checkout.planName", {
    user: billingUserMap[billingUserType],
    planPeriod: planPeriodData.selectedPeriod,
  });

  const isMobileViewCompletePaymentButtonEnabled = !(
    !plan ||
    !paymentMethod ||
    formSubmissionState.isSubmitting
  );
  const isDesktopViewCompletePaymentButtonEnabled = !(
    !isFormOpen ||
    formSubmissionState.isSubmitting ||
    (!isSubscriptionExpired &&
      !existingPaymentMethodData?.isValid &&
      !paymentMethod)
  );

  const isCompletePaymentButtonEnabled = isMobileView
    ? isMobileViewCompletePaymentButtonEnabled
    : isDesktopViewCompletePaymentButtonEnabled;

  return (
    <>
      <header>
        <PageTitle className="pl-0">
          {t("common:billing.checkoutStep.pageTitle")}
        </PageTitle>
      </header>
      {customTexts?.checkoutStepPageDescr && (
        <p className="mb-3">{t(customTexts.checkoutStepPageDescr)}</p>
      )}
      <div className="flex flex-1 flex-col">
        {!!recurlyErrorMsg && (
          <PaymentErrorModal
            handleCloseModal={() => {
              setRecurlyErrorMsg("");
            }}
            title={t("common:billing.errorPopup.title")}
            description={recurlyErrorMsg}
          />
        )}
        {!isMobileView && (
          <PriceInfo
            triggerRefetch={refetchPrices}
            addOns={planPeriodData.selectedAddOns}
            planName={planName}
            currencySymbol={currencySymbol}
            placeholderSubscription={placeholderSubscription}
            isAnnual={isAnnual}
            isReadOnlyAddon={checkoutFlow === CheckoutFlow.ChangePlan}
          />
        )}
        <div className={formWrapperClassName}>
          <SummaryBlock
            planName={planName}
            price={price}
            couponBag={couponBag}
            isMobileView={isMobileView}
            countryCode={selectedCountryCode}
            disclaimerMessage="common:billing.checkout.orderSummary.continuousAuthorityAgreement"
            customTexts={customTexts}
          />
          <BillingAndPaymentForm
            isFormOpen={isFormOpen}
            formErrors={formErrors}
            submitForm={submitForm}
            billingFormRef={htmlFormRef}
            sectionRef={sectionRef}
            setFieldValue={setFieldValue}
            isSubmitted={formSubmissionState.isSubmitted}
            formValues={formValues}
            cardDetailError={cardDetailError}
            removeInvalidCardError={removeInvalidCardError}
            isMobileView={isMobileView}
            cardFormIsVisible={cardFormIsVisible}
            isDirectDebit={isDirectDebit}
            planType={plan?.planType}
            planPeriod={planPeriodData.selectedPeriod}
            selectedMethod={paymentMethod}
            setPaymentMethod={handleChangePlanMethod}
            existingPaymentMethodData={existingPaymentMethodData!}
            existingPaymentMethodIsLoading={existingPaymentMethodIsLoading}
            existingPaymentMethodIsError={existingPaymentMethodIsError}
          />
        </div>
        <StickyCTABar
          prev={{
            text: getBackBtnText(),
            onClick: handleBackButton,
          }}
          next={{
            text: getCheckoutBtnText(),
            onClick: handleContinueButton,
            disabled: !isCompletePaymentButtonEnabled,
          }}
          isSticky={!mobileKeyboardIsOpen}
          ctaBarAdditionalClassName="max-w-full self-end"
        />
      </div>
    </>
  );
};

export default () => {
  const { planPeriodData } = useContext(BillingContext);

  const { data: summary, isLoading: summaryLoading } = useGetSummary();
  const { data: subscriptions, isLoading: subscriptionsLoading } =
    useGetSubscriptions();

  const { data: recurlyDetails, isLoading: recurlyDetailsLoading } =
    useGetRecurlyDetails();

  const isSubscriptionExpired =
    recurlyDetails?.subscription.state === ISubscriptionState.Expired;

  if (!Array.isArray(subscriptions)) return null;

  const placeholderSubscription = subscriptions.find(
    // plan is actually selected when a payment method is chosen; data is the same for each plan, just different IDs for each method
    ({ code }) => code.includes(planPeriodData.selectedPeriod || "") && code
  );

  if (subscriptionsLoading || summaryLoading || recurlyDetailsLoading)
    return <Spinner />;

  return (
    <RecurlyWrapper>
      <AddOnCheckout
        subscriptions={subscriptions}
        placeholderSubscription={placeholderSubscription}
        summary={summary}
        isSubscriptionExpired={isSubscriptionExpired}
      />
    </RecurlyWrapper>
  );
};
